import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  TablePagination
} from '@mui/material';
import { Search as SearchIcon, MarkEmailUnread as UnmatchedIcon } from '@mui/icons-material';
import BreadcrumbsNav from '../components/BreadcrumbsNav';

const UnmatchedEmailsPage = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const fetchUnmatchedEmails = async (pageNum = 0, searchTerm = '') => {
    try {
      setLoading(true);
      const params = { page: pageNum + 1, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await api.get('/email/unmatched', { params });
      setEmails(response.data.emails);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch unmatched emails:', error);
      setEmails([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnmatchedEmails(page, search);
  }, [page]);

  const handleSearch = () => {
    setPage(0);
    fetchUnmatchedEmails(0, search);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <BreadcrumbsNav />
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <UnmatchedIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4" fontWeight="bold">
          Unmatched Emails
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          This page shows emails that were sent to your domain (e.g. inbound.neoshift.ai) but did not match any
          configured project email address. For example, if someone typed project2@inbound.neoshift.ai by mistake
          instead of project1@inbound.neoshift.ai, that email appears here. These emails were not executed because
          the recipient address does not exist in any project. Records are retained for 7 days.
        </Typography>
      </Alert>

      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by sender or recipient email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
        </Box>
      </Paper>

      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date/Time</strong></TableCell>
                  <TableCell><strong>From</strong></TableCell>
                  <TableCell><strong>To (unmatched address)</strong></TableCell>
                  <TableCell><strong>Subject</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="textSecondary">
                        No unmatched emails found. Emails are retained for 7 days.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  emails.map((email) => (
                    <TableRow key={email._id}>
                      <TableCell>
                        {email.receivedAt ? new Date(email.receivedAt).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell>{email.emailFrom || '-'}</TableCell>
                      <TableCell>{email.emailTo || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {email.emailSubject || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={limit}
            rowsPerPageOptions={[limit]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          />
        )}
      </Paper>
    </Box>
  );
};

export default UnmatchedEmailsPage;
